import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import {
	Spotlight,
	SpotlightManager,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { SpotlightProps } from '@atlaskit/onboarding/types';

import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import type { EditorAPIRef } from '@confluence/loom-editor-utils';
import { withFlags, type WithFlagsProps } from '@confluence/flags';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import { fg } from '@confluence/feature-gating';

import spotlightImage from './assets/pageHeaderSpotlightImage.png';

const i18n = defineMessages({
	body: {
		id: 'loom-onboarding.editor-toolbar-button-spotlight.content',
		defaultMessage: 'Record a Loom video to add context or a content walkthrough.',
		description: 'Text for the page header Loom record button spotlight',
	},
	dismissButton: {
		id: 'loom-onboarding.editor-toolbar-button-spotlight.dismissButton',
		defaultMessage: 'Dismiss',
		description: 'Text on the button to click to indicate you are done viewing the spotlight',
	},
	tryItOutButton: {
		id: 'loom-onboarding.editor-toolbar-button-spotlight.tryItOutButton',
		defaultMessage: 'Try it out',
		description: 'Text on the button to click to try recording a Loom',
	},
});

const SCREEN_NAME = 'editorToolbarLoomButtonOnboardingSpotlight';

export type EditorToolbarLoomButtonSpotlightProps = {
	/**
	 * spotlight trigger component.
	 */
	children?: React.ReactNode;
	/**
	 * spotlight dismiss button callback.
	 */
	onClose?: () => void;
	/**
	 * Editor API Ref. Used for triggering the Loom SDK from our spotlight
	 */
	editorAPIRef: EditorAPIRef;
	/**
	 * Dictates the placement of the spotlight dialog.
	 */
	spotlightPlacement?: SpotlightProps['dialogPlacement'];
	/**
	 * Analytics attribute that will indicate where the spotlight originated from.
	 */
	nudgeSource?: string;
	/**
	 * The EP message that controls the spotlight.
	 */
	epMessage: string;
	/**
	 * Shape of loom icon - default is square
	 */
	isCircle?: boolean;
};

const EditorToolbarLoomButtonSpotlightComponent = ({
	children,
	editorAPIRef,
	spotlightPlacement,
	nudgeSource,
	epMessage,
	flags,
	isCircle,
}: EditorToolbarLoomButtonSpotlightProps & WithFlagsProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLoomRecorderInitialized, hasRecordingAccess } = useLoomRecorderEntryPoint({
		flags,
		entryPointLocation: 'editor-spotlight',
	});
	const [isLoomGAOnboardingActive, stopLoomGAOnboarding] = useSkippableCoordination(
		epMessage,
		!fg('confluence_m25_onboarding_spotlights') ||
			!(isLoomRecorderInitialized && hasRecordingAccess),
	);
	const [isClosed, setIsClosed] = useState<boolean>(false);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: SCREEN_NAME,
				attributes: {
					nudgeSource,
				},
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const closeSpotlight = useCallback(() => {
		if (!isClosed) {
			void stopLoomGAOnboarding();
			setIsClosed(true);
		}
	}, [isClosed, setIsClosed, stopLoomGAOnboarding]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: SCREEN_NAME,
				attributes: {
					nudgeSource,
				},
			},
		}).fire();
		closeSpotlight();
	}, [closeSpotlight, createAnalyticsEvent, nudgeSource]);

	const handleTryItOut = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryItOut',
				source: SCREEN_NAME,
				attributes: {
					nudgeSource,
				},
			},
		}).fire();

		if (editorAPIRef.current && editorAPIRef.current?.loom && editorAPIRef.current?.analytics) {
			editorAPIRef.current.core?.actions.execute(
				editorAPIRef.current.loom.actions.recordVideo({
					inputMethod: INPUT_METHOD.BUTTON,
					editorAnalyticsAPI: editorAPIRef.current.analytics.actions,
				}),
			);
		}

		closeSpotlight();
	}, [closeSpotlight, createAnalyticsEvent, editorAPIRef, nudgeSource]);

	if (isLoomGAOnboardingActive && !isClosed && fg('confluence_m25_onboarding_spotlights')) {
		return (
			<SpotlightManager>
				<SpotlightTarget name="editor-toolbar-loom-button-spotlight">{children}</SpotlightTarget>
				<SpotlightTransition>
					<Spotlight
						target="editor-toolbar-loom-button-spotlight"
						targetBgColor={token('color.text.inverse')}
						targetRadius={isCircle ? 24 : 6}
						dialogWidth={275}
						image={spotlightImage}
						dialogPlacement={spotlightPlacement}
						actions={[
							{
								text: intl.formatMessage(i18n.tryItOutButton),
								onClick: handleTryItOut,
							},
							{
								text: intl.formatMessage(i18n.dismissButton),
								onClick: handleDismiss,
								appearance: 'subtle',
							},
						]}
					>
						<FormattedMessage {...i18n.body} />
					</Spotlight>
				</SpotlightTransition>
			</SpotlightManager>
		);
	}

	return <>{children}</>;
};

export const EditorToolbarLoomButtonSpotlight = withFlags(
	EditorToolbarLoomButtonSpotlightComponent,
);
